import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {
  fetchWithErrorHandling,
  throwErrorsIfBadResponse,
  parseJSONWithBetterErrors,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {create} from '@github-ui/dotcom-schema/protobuf'
import {
  ProjectsResponseSchema,
  PullRequestProjectSchema,
  PullRequestProjectFieldSchema,
  PullRequestProjectFieldType,
  type ProjectsResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/projects'

import {useRouteQueryKey} from '@github-ui/react-core/future/use-route-query-key'
import {pullRequestsOverviewRoute} from '../routes/overview-route'

// The shape of `value` required depends on the target field's type: a string for text/date/
// single-select/iteration fields, a number for number fields, an array of option ids for
// multi-select fields, or null/undefined/'' to clear the field's value.
export type MutationData = {
  projectId: string
  itemId: string
  fieldId: string
  value: string | number | string[] | null | undefined
}

type Callbacks = {
  onError?: (error: Error) => void
}

export function useUpdateProjectField({onError}: Callbacks = {}) {
  const projectsQueryKey = useRouteQueryKey(pullRequestsOverviewRoute, 'projects')
  const updateProjectFieldUrl = usePageDataUrl(PageData.updateProjectField)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: MutationData) => {
      const response = await fetchWithErrorHandling(updateProjectFieldUrl, {
        method: 'PUT',
        headers: {Accept: 'application/json'},
        body: {itemId: data.itemId, fieldId: data.fieldId, value: data.value ?? null},
      })
      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)
    },
    onMutate: async data => {
      if (!projectsQueryKey) return

      await queryClient.cancelQueries({queryKey: projectsQueryKey})
      const previous = queryClient.getQueryData<ProjectsResponse>(projectsQueryKey)

      if (previous) {
        queryClient.setQueryData<ProjectsResponse>(projectsQueryKey, () =>
          create(ProjectsResponseSchema, {
            projects: previous.projects.map(project => {
              if (project.id !== data.projectId) return project

              return create(PullRequestProjectSchema, {
                ...project,
                fields: project.fields.map(field =>
                  field.fieldId === data.fieldId
                    ? create(PullRequestProjectFieldSchema, {...field, ...fieldValuePatch(field.type, data.value)})
                    : field,
                ),
              })
            }),
          }),
        )
      }

      return {previous}
    },
    onError: (e: Error, _vars, context) => {
      if (projectsQueryKey && context?.previous) {
        queryClient.setQueryData(projectsQueryKey, context.previous)
      }
      onError?.(e)
    },
    onSettled: () => {
      if (!projectsQueryKey) return
      return queryClient.invalidateQueries({queryKey: projectsQueryKey})
    },
  })
}

// Builds the subset of PullRequestProjectField properties that represent `value`, matching
// whichever one the field's type populates.
function fieldValuePatch(type: PullRequestProjectFieldType, value: MutationData['value']) {
  switch (type) {
    case PullRequestProjectFieldType.TEXT:
      return {textValue: typeof value === 'string' ? value : ''}
    case PullRequestProjectFieldType.NUMBER:
      return {numberValue: typeof value === 'number' ? value : undefined}
    case PullRequestProjectFieldType.DATE:
      return {dateValue: typeof value === 'string' ? value : ''}
    case PullRequestProjectFieldType.SINGLE_SELECT:
      return {optionId: typeof value === 'string' ? value : ''}
    case PullRequestProjectFieldType.MULTI_SELECT:
      return {optionIds: Array.isArray(value) ? value : []}
    case PullRequestProjectFieldType.ITERATION:
      return {iterationId: typeof value === 'string' ? value : ''}
    default:
      return {}
  }
}
